import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Input types that must never be uppercased.
 *
 * Passwords are case-sensitive — uppercasing one silently makes it impossible
 * to sign in. Emails are lowercased by the API for matching, so uppercasing
 * here would just fight it. The rest have no meaningful case.
 */
const NEVER_UPPERCASE = new Set([
  'password',
  'email',
  'url',
  'number',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
  'file',
  'search',
  'color',
  'range',
  'checkbox',
  'radio',
]);

/**
 * Autocomplete hints that mark a field as a credential.
 *
 * A password field with a reveal toggle flips `type` to "text" while the
 * password is visible, which put it outside `NEVER_UPPERCASE` and silently
 * uppercased anything typed with the eye open — a lower-case password entered
 * that way could never sign in. `autoComplete` does not change when the eye is
 * clicked, so it is the stable signal that the value is case-sensitive.
 */
const CREDENTIAL_AUTOCOMPLETE = new Set([
  'current-password',
  'new-password',
  'one-time-code',
  'username',
]);

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Force the entered value to upper case. Defaults to true for text-like
   * inputs; pass `false` to opt a field out.
   */
  uppercase?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', uppercase, onChange, autoComplete, ...props }, ref) => {
    const shouldUppercase =
      uppercase ??
      (!NEVER_UPPERCASE.has(type) && !CREDENTIAL_AUTOCOMPLETE.has(autoComplete ?? ''));

    return (
      <input
        ref={ref}
        type={type}
        autoComplete={autoComplete}
        onChange={(event) => {
          if (shouldUppercase) {
            const element = event.currentTarget;
            const next = element.value.toUpperCase();

            if (next !== element.value) {
              // Rewriting `value` moves the caret to the end, which makes it
              // impossible to correct a typo mid-word. `toUpperCase` preserves
              // length for the Latin range, so the original offsets still hold
              // and can simply be restored.
              const { selectionStart, selectionEnd } = element;
              element.value = next;
              if (selectionStart !== null && next.length === element.value.length) {
                element.setSelectionRange(selectionStart, selectionEnd);
              }
            }
          }

          // Called after the mutation so React Hook Form stores the uppercased
          // value, not what was typed.
          onChange?.(event);
        }}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
          // Placeholders stay sentence case — uppercasing them makes every
          // empty field shout.
          'placeholder:text-muted-foreground placeholder:normal-case',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          // A red ring on an invalid field, driven by the DOM rather than a
          // prop, so <FormField> only has to set aria-invalid.
          'aria-[invalid=true]:border-destructive aria-[invalid=true]:outline-destructive',
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          className,
        )}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';
